import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../services/user.service';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  requireProfile?: boolean;
}

/**
 * ProtectedRoute component handles route protection based on authentication status, 
 * user role, and profile completion status.
 * 
 * @param children Component to render if user is authorized
 * @param requiredRoles Array of roles allowed to access this route
 * @param requireProfile Whether a completed profile is required to access this route
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [], 
  requireProfile = false 
}) => {
  const { authenticated, loading, role, hasProfile } = useAuth();
  const location = useLocation();

  if (loading) {
    // Show loading spinner while auth state is being determined
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // If user is not authenticated, redirect to marketing page
  if (!authenticated) {
    return <Navigate to="/" replace />;
  }

  // If route requires specific roles and user doesn't have one of them
  if (requiredRoles.length > 0 && role && !requiredRoles.includes(role)) {
    // If user is waitlisted, send them to the waitlist page
    if (role === 'waitlisted') {
      return <Navigate to="/waitlist" replace />;
    }
    // Otherwise send them to an unauthorized page
    return <Navigate to="/unauthorized" replace />;
  }

  // If route requires a complete profile but user doesn't have one
  if (requireProfile && !hasProfile) {
    return <Navigate to="/update-profile" replace />;
  }

  // User meets all requirements, render the protected component
  return <>{children}</>;
};

export default ProtectedRoute;